import axios, { type AxiosInstance } from 'axios'
import { attachAuthInterceptors } from '@/api/interceptors/authInterceptor'
import { attachCaseTransformerInterceptors } from '@/api/interceptors/caseTransformerInterceptor'
import { i18n } from '@/locales'

const createApiClient = (options?: { baseURL?: string }): AxiosInstance => {
  const client = axios.create({
    baseURL: options?.baseURL ?? import.meta.env.VITE_API_URL,
    withCredentials: true
  })

  client.interceptors.request.use((config) => {
    config.headers['Accept-Language'] = i18n.global.locale.value
    return config
  })

  attachCaseTransformerInterceptors(client)
  attachAuthInterceptors(client)

  return client
}

/**
 * The API root, with any version segment stripped.
 *
 * `VITE_API_URL` is one deployment-time value, but the app talks to two
 * versions at once and will do for a long time: 35 modules on v0, the
 * redesigned product page on v1. So the version is not something the env var
 * gets to decide — the root is, and each client appends its own.
 *
 * This accepts the value with `/v0/`, with `/v1/`, or with no version at all,
 * because the alternative is a build that looks fine and 404s at runtime.
 */
export const apiRoot = (base: string): string => {
  const withoutVersion = base.replace(/\/v\d+\/?$/, '')
  return withoutVersion.endsWith('/') ? withoutVersion : `${withoutVersion}/`
}

export const versionedBaseUrl = (version: 'v0' | 'v1'): string =>
  `${apiRoot(import.meta.env.VITE_API_URL ?? '/api/')}${version}/`

export const apiClient = createApiClient({ baseURL: versionedBaseUrl('v0') })

/**
 * `/api/v1` — the redesigned product page, and nothing else yet.
 *
 * Its own client rather than a path prefix on the v0 one: v0 is frozen, and a
 * page that has not migrated must not have its wire changed because another
 * page did.
 */
export const apiV1Client = createApiClient({ baseURL: versionedBaseUrl('v1') })

export { createApiClient }
